// main imports
import React, { Component } from "react";

import { getBackend } from "../../lib/backend/Backend2";
import * as sf from "../../lib/StockForecast";
import * as t from "../../lib/backend/manufacturing2.generated.types";

import { translate } from "../IntlProviderWrapper";

import MaterialStockForecast, { MaterialTableId } from "./MaterialStockForecast";
import * as BusinessLogic from "../../lib/BusinessLogic";
import local_settings from "../../lib/LocalSettings";

import { LINE_GROUP_TAGS_ACCESS, PLANT_TAGS_ACCESS } from "../../lib/ManufacturingTags.generated";
import Loader from "../Loader";
import type { ILineData } from "../../lib/backend/manufacturing2.generated.types";

import { getMaxDaysForHorizon } from "./StocksViewCommon";
import { dateFromDayShift, deepEqual, shiftNumber } from "../../lib/Util";
import { FIELD_ALLOW_USAGE_OF_SAFETY_STOCK } from "../../lib/LocalSettings";

import type { AggregationTimeWindow } from "./MaterialStockForecast";

// navigation filter
export type DisplayType = {
    code: string;
    title: string;
};

export type StockForecastFilterProps = {
    display_types?: DisplayType[];
    selected_display_type: sf.MaterialForecastType;
    subtypes?: DisplayType[];
    selected_subtype: sf.MaterialForecastSubtype;
    locations?: DisplayType[];
    title: string;
    only_accounted_for_initial_stocks?: DisplayType[];
    only_accounted_for_initial_stock: sf.MaterialForecastOnlyAccounted;
    safety_stock_show: boolean;
    safety_stock_can_be_used: boolean;
    selected_location: sf.MaterialForecastLocation;
    selected_line_uuid: string;
    lines: ILineData[];
    order_external_ids?: string[];
    selected_aggregate: string;
};

export type SelectedValueLabelDefaultFilter = "order_external_id" | "line" | "selected_display_type";

export const storeAllowUsageOfSafetyStock = (value: boolean) => {
    local_settings.setItem(FIELD_ALLOW_USAGE_OF_SAFETY_STOCK, value ? "true" : "false");
};

export const getAllowUsageOfSafetyStock = (): boolean => {
    return local_settings.getItem(FIELD_ALLOW_USAGE_OF_SAFETY_STOCK) === "true";
};

export function initStockForecastFilterProps(line_group_uuid: string): StockForecastFilterProps {
    const line_group_tags = BusinessLogic.getLineGroupTags(line_group_uuid);
    // check if shopfloor toggle is set to true by default
    const shopfloor_default: boolean = LINE_GROUP_TAGS_ACCESS.stock_forecast_shopfloor_option_default(line_group_tags);
    // check if we show safety stock details
    const safety_stock_show: boolean = LINE_GROUP_TAGS_ACCESS.stock_forecast_show_safety_stock(line_group_tags);
    const withdrawn_and_unaccounted: boolean =
        LINE_GROUP_TAGS_ACCESS.stock_forecast_withdrawn_and_unaccounted_default(line_group_tags);

    // check WIP flag for plants in line group
    // this is slightly different than on planning table, where only current plant is used.
    const line_group = BusinessLogic.getLineGroupForUser(line_group_uuid);
    const plant_uuids = line_group?.plant_uuids ?? [];
    const enable_wip_stock_tracking = plant_uuids.some(plant_uuid =>
        PLANT_TAGS_ACCESS.enable_wip_stock_tracking(BusinessLogic.getPlantTags(plant_uuid))
    );
    const display_types: DisplayType[] = [
        {
            code: sf.MATERIAL_FORECAST_TYPES.out,
            title: translate("Manufacturing.MaterialStockForecast.material_out"),
        },
        {
            code: sf.MATERIAL_FORECAST_TYPES.in,
            title: translate("Manufacturing.MaterialStockForecast.material_in"),
        },
        ...(enable_wip_stock_tracking
            ? [
                  {
                      code: sf.MATERIAL_FORECAST_TYPES.wip,
                      title: translate("Manufacturing.MaterialStockForecast.material_wip"),
                  },
              ]
            : []),
    ];

    return {
        display_types,
        selected_display_type: sf.MATERIAL_FORECAST_TYPES.out,
        subtypes: [
            {
                code: sf.MATERIAL_FORECAST_SUBTYPES.combined,
                title: translate("Manufacturing.MaterialStockForecast.combined"),
            },
            {
                code: sf.MATERIAL_FORECAST_SUBTYPES.consumed,
                title: translate("Manufacturing.MaterialStockForecast.consumed_cumulative"),
            },
            {
                code: sf.MATERIAL_FORECAST_SUBTYPES.consumedByShift,
                title: translate("Manufacturing.MaterialStockForecast.consumed_by_shift"),
            },
            {
                code: sf.MATERIAL_FORECAST_SUBTYPES.produced,
                title: translate("Manufacturing.MaterialStockForecast.produced_cumulative"),
            },
            {
                code: sf.MATERIAL_FORECAST_SUBTYPES.producedByShift,
                title: translate("Manufacturing.MaterialStockForecast.produced_by_shift"),
            },
            {
                code: sf.MATERIAL_FORECAST_SUBTYPES.receivedByShift,
                title: translate("Manufacturing.MaterialStockForecast.received_by_shift"),
            },
            {
                code: sf.MATERIAL_FORECAST_SUBTYPES.required,
                title: translate("Manufacturing.MaterialStockForecast.required_cumulative"),
            },
            {
                code: sf.MATERIAL_FORECAST_SUBTYPES.requiredByShift,
                title: translate("Manufacturing.MaterialStockForecast.required_by_shift"),
            },
        ],
        selected_subtype: sf.MATERIAL_FORECAST_SUBTYPES.combined,
        locations: [
            {
                code: sf.MATERIAL_FORECAST_LOCATIONS.shopfloor,
                title: translate("Manufacturing.MaterialStockForecast.shopfloor"),
            },
            {
                code: sf.MATERIAL_FORECAST_LOCATIONS.plant,
                title: translate("Manufacturing.MaterialStockForecast.plant"),
            },
        ],
        only_accounted_for_initial_stocks: [
            {
                code: "yes",
                title: translate("common.yes", "Yes"),
            },
            {
                code: "no",
                title: translate("common.no", "No"),
            },
        ],
        only_accounted_for_initial_stock: withdrawn_and_unaccounted
            ? sf.MATERIAL_FORECAST_ONLY_ACCOUNTED.no
            : sf.MATERIAL_FORECAST_ONLY_ACCOUNTED.yes,
        safety_stock_show,
        safety_stock_can_be_used: getAllowUsageOfSafetyStock(),
        selected_location: shopfloor_default
            ? sf.MATERIAL_FORECAST_LOCATIONS.shopfloor
            : sf.MATERIAL_FORECAST_LOCATIONS.plant,
        selected_line_uuid: "",
        selected_aggregate: "shift",
        lines: [],
        order_external_ids: [],
        title: "",
    };
}

type Props = {
    line_group_uuid: string;
    line_uuid?: string;
    filter: StockForecastFilterProps;
    handleStockForecastDisplayTypeChange?: (value: string) => void;
    changeSelectedLineUUID?: (value: string) => void;
    onChangeOrderExternalId?: (value: string) => void;
    setDefaultFilter?: (type: SelectedValueLabelDefaultFilter) => () => void;
    changeSelectedHorizonStockForecast?: (value: AggregationTimeWindow) => void;
    onTitleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    hide_display_type_filter?: boolean;
    hide_lines_filter?: boolean;
    selected_aggregate?: string;
    selected_material_types?: Record<MaterialTableId, Set<string>>;
    onMaterialTypeChangeForTable?: (table_id: MaterialTableId) => (values: Set<string>) => void;
};

type State = {
    error: string;
    loading: boolean;
    shift_headers: sf.MaterialForecastShift[];
    show_affiliated_stock: boolean;
    combined_materials_changing: t.IMaterialForecast[];
    combined_materials_nonchanging: t.IMaterialForecast[];
    combined_materials_zero: t.IMaterialForecast[];
    combined_forecast_params: sf.MaterialForecastParams | null;
    materials_changing: t.IMaterialForecast[];
    materials_nonchanging: t.IMaterialForecast[];
    materials_zero: t.IMaterialForecast[];
    report_id: string;
};

class MaterialStockForecastView extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            error: "",
            loading: true,
            shift_headers: [],
            show_affiliated_stock: false,
            combined_forecast_params: null,
            combined_materials_changing: [],
            combined_materials_nonchanging: [],
            combined_materials_zero: [],
            materials_changing: [],
            materials_nonchanging: [],
            materials_zero: [],
            report_id: "",
        };
    }

    componentDidMount() {
        this.loadComponent();
    }

    componentDidUpdate(prev_props: Props) {
        const order_external_id_changed =
            prev_props.filter.order_external_ids !== this.props.filter.order_external_ids &&
            ((prev_props.filter.order_external_ids !== undefined && prev_props.filter.order_external_ids.length > 0) ||
                (this.props.filter.order_external_ids !== undefined &&
                    this.props.filter.order_external_ids.length > 0));

        if (
            (prev_props.line_group_uuid !== this.props.line_group_uuid ||
                prev_props.line_uuid !== this.props.line_uuid ||
                prev_props.filter.selected_display_type !== this.props.filter.selected_display_type ||
                prev_props.filter.selected_subtype !== this.props.filter.selected_subtype ||
                prev_props.filter.only_accounted_for_initial_stock !==
                    this.props.filter.only_accounted_for_initial_stock ||
                prev_props.filter.selected_location !== this.props.filter.selected_location ||
                order_external_id_changed) &&
            this.props.line_group_uuid !== ""
        ) {
            this.loadComponent();
        }

        if (prev_props.filter.selected_aggregate !== this.props.filter.selected_aggregate) {
            this.recalculateShiftHeadersAndMaterials();
        }
    }

    recalculateShiftHeadersAndMaterials = () => {
        const { line_group_uuid, filter } = this.props;

        const materials_all = [
            ...this.state.materials_changing,
            ...this.state.materials_nonchanging,
            ...this.state.materials_zero,
        ];
        const forecast_horizon_days = getMaxDaysForHorizon(line_group_uuid, filter.selected_aggregate);

        const { materials_changing, materials_nonchanging, materials_zero } = sf.groupMaterialForecasts(
            materials_all,
            forecast_horizon_days
        );
        const forecast_shifts = sf.getMaterialForecastShifts(materials_all, forecast_horizon_days);

        this.setState({
            shift_headers: this.shiftAggregationFix(forecast_shifts),
            materials_changing,
            materials_nonchanging,
            materials_zero,
        });
    };

    getForecastParams = (params: Partial<sf.MaterialForecastParams> = {}): sf.MaterialForecastParams => {
        const { line_group_uuid, line_uuid } = this.props;
        const {
            selected_display_type,
            selected_subtype,
            only_accounted_for_initial_stock,
            selected_location,
            order_external_ids,
            selected_aggregate,
        } = this.props.filter;

        return {
            group_uuid: line_group_uuid,
            type: selected_display_type,
            subtype: selected_subtype,
            only_accounted_for_initial_stock,
            location: selected_location,
            forecast_horizon_days: getMaxDaysForHorizon(line_group_uuid, selected_aggregate),
            line_uuid,
            order_external_ids,
            material_types: [],
            ...params,
        };
    };

    getMaterialForecastForShifts = async (
        material_external_id: string,
        index: number,
        report_id: string
    ): Promise<t.IGetMaterialForecastForShiftsRes> => {
        const { line_group_uuid, line_uuid } = this.props;
        const {
            selected_aggregate,
            selected_display_type,
            only_accounted_for_initial_stock,
            selected_location,
            order_external_ids,
        } = this.props.filter;
        let subtypes = ["produced_by_shift", "consumed_by_shift"];

        if (selected_location !== "shopfloor") {
            subtypes = [...subtypes, "received_by_shift", "required_by_shift"];
        }

        let start_index = index;
        if (selected_aggregate !== "shift") {
            let shift_header = this.state.shift_headers[start_index];
            while (
                start_index > 0 &&
                ((selected_aggregate === "day" && shift_header.shift > 0) ||
                    (selected_aggregate === "week" && (shift_header.day_of_week > 0 || shift_header.shift > 0)))
            ) {
                start_index -= 1;
                shift_header = this.state.shift_headers[start_index];
            }
        }

        const start_header = this.state.shift_headers[start_index];
        const start_shift = shiftNumber(dateFromDayShift(new Date(start_header.date), start_header.shift));
        const end_header = this.state.shift_headers[index];
        const end_shift = shiftNumber(dateFromDayShift(new Date(end_header.date), end_header.shift));
        const res = await getBackend().manufacturing.getMaterialForecastForShifts({
            group_uuid: line_group_uuid,
            line_uuid,
            report_id,
            type: selected_display_type,
            subtypes,
            only_accounted_for_initial_stock,
            location: selected_location,
            order_external_ids,
            material_external_id,
            start_shift: start_shift.shift_tag,
            end_shift: end_shift.shift_tag,
        });

        return res;
    };

    shiftAggregationFix = (shift_headers: sf.MaterialForecastShift[]) => {
        // if one of the last three headers has a different date, then we cut it out
        // otherwise day aggregation cannot be calculated
        if (this.props.filter.selected_aggregate === "day" && shift_headers.length > 3) {
            const headers = shift_headers.slice(shift_headers.length - 3, shift_headers.length);

            let lastValidDateIdx: number | null = null;

            for (let i = 0; i < headers.length - 2; i++) {
                if (headers[i] !== headers[i + 1]) {
                    lastValidDateIdx = i;
                    break;
                }
            }

            if (lastValidDateIdx !== null) {
                shift_headers = shift_headers.slice(0, shift_headers.length - 3 + lastValidDateIdx);
            }
        }

        return shift_headers;
    };

    async loadComponent() {
        try {
            if (this.props.line_group_uuid !== null) {
                this.setState({ loading: true });

                const forecast_params = this.getForecastParams();
                const { forecast_shifts, materials_changing, materials_nonchanging, materials_zero, report_id } =
                    await sf.getMaterialForecast(forecast_params);

                let {
                    combined_forecast_params,
                    combined_materials_changing,
                    combined_materials_nonchanging,
                    combined_materials_zero,
                } = this.state;

                const is_combined_forecast_selected = forecast_params.subtype === "combined";
                if (is_combined_forecast_selected) {
                    combined_forecast_params = forecast_params;
                    combined_materials_changing = materials_changing;
                    combined_materials_nonchanging = materials_nonchanging;
                    combined_materials_zero = materials_zero;
                }

                if (!is_combined_forecast_selected) {
                    const new_combined_forecast_params = {
                        ...forecast_params,
                        subtype: "combined",
                    } as sf.MaterialForecastParams;

                    if (
                        combined_forecast_params === null ||
                        !deepEqual(combined_forecast_params, new_combined_forecast_params)
                    ) {
                        const forecast_combined = await sf.getMaterialForecast(new_combined_forecast_params);
                        combined_forecast_params = new_combined_forecast_params;
                        combined_materials_changing = forecast_combined.materials_changing;
                        combined_materials_nonchanging = forecast_combined.materials_nonchanging;
                        combined_materials_zero = forecast_combined.materials_zero;
                    }
                }

                // currently show_affiliated_stock is set if at least one
                // affiliated_stock is nonzero. Alternative: control this by
                // a tag and bring it from backend
                const show_affiliated_stock =
                    materials_changing.some(x => x.affiliated_stock > 0) ||
                    materials_nonchanging.some(x => x.affiliated_stock > 0) ||
                    materials_zero.some(x => x.affiliated_stock > 0);
                this.setState({
                    shift_headers: this.shiftAggregationFix(forecast_shifts),
                    show_affiliated_stock,
                    materials_changing,
                    materials_nonchanging,
                    materials_zero,
                    combined_materials_changing,
                    combined_materials_nonchanging,
                    combined_materials_zero,
                    combined_forecast_params,
                    report_id,
                });
            }
        } catch (err: unknown) {
            this.setState({ error: (err as Error).message });
        }
        this.setState({ loading: false });
    }

    render() {
        return (
            <article className="article">
                <section id="statistics" className="data_sources">
                    <div className="vertical_tab">
                        <div className="container-fluid" style={{ width: "fit-content" }}>
                            <div className="row" style={{ display: "block", textAlign: "center" }}>
                                <div className="co-md-12 text-center">
                                    {this.state.loading ? (
                                        <Loader />
                                    ) : (
                                        this.state.report_id && (
                                            <MaterialStockForecast
                                                shift_headers={this.state.shift_headers}
                                                show_affiliated_stock={this.state.show_affiliated_stock}
                                                safety_stock_show={this.props.filter.safety_stock_show}
                                                safety_stock_account_for={this.props.filter.safety_stock_can_be_used}
                                                materials_changing={this.state.materials_changing}
                                                materials_nonchanging={this.state.materials_nonchanging}
                                                materials_zero={this.state.materials_zero}
                                                selected_subtype={this.props.filter.selected_subtype}
                                                selected_aggregate={this.props.filter.selected_aggregate || "shift"}
                                                selected_display_type={this.props.filter.selected_display_type}
                                                only_accounted_for_initial_stock={
                                                    this.props.filter.only_accounted_for_initial_stock
                                                }
                                                selected_location={this.props.filter.selected_location}
                                                forecasts_materials_changing={this.state.combined_materials_changing}
                                                forecasts_materials_nonchanging={
                                                    this.state.combined_materials_nonchanging
                                                }
                                                forecasts_materials_zero={this.state.combined_materials_zero}
                                                getForecast={this.getMaterialForecastForShifts}
                                                stock_forecast_filter={this.props.filter}
                                                handleStockForecastDisplayTypeChange={
                                                    this.props.handleStockForecastDisplayTypeChange
                                                }
                                                changeSelectedLineUUID={this.props.changeSelectedLineUUID}
                                                onChangeOrderExternalId={this.props.onChangeOrderExternalId}
                                                setDefaultFilter={this.props.setDefaultFilter}
                                                onTitleChange={this.props.onTitleChange}
                                                changeSelectedHorizonStockForecast={
                                                    this.props.changeSelectedHorizonStockForecast
                                                }
                                                hide_display_type_filter={this.props.hide_display_type_filter}
                                                hide_lines_filter={this.props.hide_lines_filter}
                                                report_id={this.state.report_id}
                                                selected_material_types={this.props.selected_material_types}
                                                onMaterialTypeChangeForTable={this.props.onMaterialTypeChangeForTable}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </article>
        );
    }
}

export default MaterialStockForecastView;
